import { useMemo } from 'react'
import { HUNTABLE_GAME_CATALOG, type HuntableGameSpecies } from '@wehunt/ui'
import { useRemoteConfigString } from '../../../shared/remoteConfig/useRemoteConfigString'

/**
 * Raw Firebase Remote Config shape for the `gameNames` parameter — keyed by
 * numeric-string IDs the mobile apps use for game-report categorization.
 * The web wizard doesn't care about the IDs; it just flattens to a list.
 */
interface RawGameNamesEntry {
  name?: Partial<Record<string, string>> & { base?: string }
  type?: string
  subtypes?: Array<{
    key?: string
    name?: Partial<Record<string, string>> & { base?: string }
  }>
}

type RawGameNames = Record<string, RawGameNamesEntry>

/**
 * Fetches the Remote Config `gameNames` catalog and returns it in the same
 * `HuntableGameSpecies` shape the wizard already consumes. Species + class
 * display names come from `name.en` with `name.base` as the fallback, so
 * existing drafts that store base-English names round-trip cleanly.
 *
 * Robustness:
 *   - AUTOTEST entries (added by QA tooling) are filtered out by name prefix.
 *   - Entries without an English (or `base`) name are dropped — those are
 *     stale Remote Config fixtures lacking a real species name.
 *   - Any fetch / parse / activate failure falls back to the bundled
 *     `HUNTABLE_GAME_CATALOG` so the wizard still works offline or when
 *     Remote Config is unavailable. The state never surfaces an error to
 *     the user; the picker just degrades to the pre-RC catalog.
 */
export function useGameNamesCatalog(): {
  catalog: readonly HuntableGameSpecies[]
  isLoading: boolean
} {
  const { value, ready } = useRemoteConfigString('gameNames')
  const catalog = useMemo<readonly HuntableGameSpecies[]>(() => {
    if (!value) return HUNTABLE_GAME_CATALOG
    try {
      return parseGameNames(JSON.parse(value) as RawGameNames)
    } catch {
      return HUNTABLE_GAME_CATALOG
    }
  }, [value])
  return { catalog, isLoading: !ready }
}

const AUTOTEST_PREFIX = /^autotest\b/i

/**
 * Pure transform from Remote Config shape to the wizard's catalog shape.
 * Exported for unit testing; the hook is just the react-query wrapper.
 */
export function parseGameNames(raw: RawGameNames): HuntableGameSpecies[] {
  // Keyed by display name, because that is the identity the pickers use: the
  // hunt-plan game step keys its selections by `species` name, so two entries
  // sharing a display name would render as two indistinguishable rows that
  // check and uncheck together. Remote Config really does hold such pairs
  // (two ids both displaying as "Other"), so they are merged here into one row.
  const byDisplayName = new Map<string, { name: string; classes: string[]; baseName?: string; gameType?: 'mammal' | 'bird' }>()
  for (const entry of Object.values(raw)) {
    const speciesName = pickDisplay(entry.name)
    if (!speciesName) continue
    if (AUTOTEST_PREFIX.test(speciesName)) continue
    const classes: string[] = []
    for (const sub of entry.subtypes ?? []) {
      const className = pickDisplay(sub.name)
      if (className) classes.push(className)
    }
    // Carry the wire identity (`name.base`) and kingdom (`type`) through so the
    // downed-game picker can build a valid game pin. Only 'mammal'/'bird' are
    // recognized game types (mobile throws on any other); anything else leaves
    // `gameType` undefined, which excludes the species from that picker.
    const baseName = entry.name?.base?.trim() || undefined
    const gameType = entry.type === 'mammal' || entry.type === 'bird' ? entry.type : undefined

    const existing = byDisplayName.get(speciesName)
    if (!existing) {
      byDisplayName.set(speciesName, { name: speciesName, classes, baseName, gameType })
      continue
    }
    // Merge: union the classes, since a duplicate id may carry extra subtypes.
    for (const cls of classes) {
      if (!existing.classes.includes(cls)) existing.classes.push(cls)
    }
    // `baseName` + `gameType` are ONE identity — a pin carries both, and a wire
    // name from one entry paired with a kingdom from another names a species
    // that doesn't exist. So they move together, taken from the first duplicate
    // that has both; a species whose first id is missing one still becomes
    // reportable, and one where no id has both stays (correctly) excluded from
    // the downed-game picker.
    const identityIncomplete = existing.baseName === undefined || existing.gameType === undefined
    if (identityIncomplete && baseName !== undefined && gameType !== undefined) {
      existing.baseName = baseName
      existing.gameType = gameType
    }
  }
  const out: HuntableGameSpecies[] = [...byDisplayName.values()]
  // Sort by display name for a stable UI order — Remote Config returns the
  // object in insertion order which is brittle across CMS edits.
  out.sort((a, b) => a.name.localeCompare(b.name))
  return out
}

function pickDisplay(name: RawGameNamesEntry['name'] | undefined): string | null {
  if (!name) return null
  // Require an explicit English translation. Curated catalog entries all
  // have `name.en` set; stale Remote Config fixtures (lowercase dupes,
  // "test", QA test data lacking translations) have only `name.base` and
  // would otherwise slip through. When the web app gains a locale provider
  // this picks `name[currentLocale] ?? name.en`, still skipping entries
  // that have neither.
  const candidate = name.en
  if (typeof candidate !== 'string') return null
  const trimmed = candidate.trim()
  return trimmed.length > 0 ? trimmed : null
}
